/**
 * Wrapper na fetch — obsluguje:
 * - Wspolny base URL (proxy w dev → /api, /auth, /health; same-origin w prod)
 * - credentials: 'include' (cookie CF Access)
 * - Parsowanie JSON
 * - Globalna logika 401 (redirect do CF Access login)
 * - Typowane bledy (ApiError z @coreltb/shared)
 */

import type { ApiError, ApiErrorCode, ApiResponse, Paginated } from '@coreltb/shared/types';
import { describeValidationError } from './validationMessage';

/**
 * Build-time API base URL.
 * - Dev (Vite proxy): pusty → relatywne /api, /auth, /health proxy do localhost:8787
 * - Prod (pages.dev): pusty → relatywne (Pages Function proxy lub bezposrednio
 *   gdy oba na *.coreltb.pl)
 * - Manualne override: VITE_API_URL=https://api.coreltb.pl
 */
const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

/** Buduje pelen URL do API. Eksportowane dla casow ktore nie ida przez
    apiFetch — np. multipart upload (FormData), download/preview attribute href. */
export function buildUrl(path: string): string {
  if (!API_BASE) return path;
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

export class ApiClientError extends Error {
  constructor(
    public status: number,
    public code: ApiErrorCode | string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** JSON body — zostanie zserializowane */
  body?: unknown;
  /** Query string params */
  query?: Record<string, string | number | boolean | undefined | null>;
}

/**
 * Wykonuje request do API. Rzuca ApiClientError przy bledach 4xx/5xx.
 * Dla 401: pokazuje redirect na CF Access login (zewnatrz nasze appki).
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, query, headers, ...rest } = options;

  // Build URL z query params (uwzgledniajac VITE_API_URL przez buildUrl)
  let url = buildUrl(path);
  if (query) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    }
    const qs = params.toString();
    if (qs) url += (url.includes('?') ? '&' : '?') + qs;
  }

  const finalHeaders = new Headers(headers);
  if (body !== undefined && !finalHeaders.has('Content-Type')) {
    finalHeaders.set('Content-Type', 'application/json');
  }
  finalHeaders.set('Accept', 'application/json');

  // Retry transient errors (503 D1 network drop, network failure).
  // Backend zwraca 503 z `Retry-After: 1` dla `D1_ERROR: Network connection lost.`
  // Method-aware: GET/HEAD zawsze idempotent, retry safe.
  // POST/PATCH/DELETE - skip retry zeby nie dublowac mutacji.
  const method = (rest.method ?? 'GET').toUpperCase();
  const isIdempotent = method === 'GET' || method === 'HEAD';
  const MAX_RETRY = isIdempotent ? 2 : 0; // total 3 attempts dla GET, 1 dla mutacji

  let response: Response | null = null;
  let lastNetworkError: unknown = null;
  for (let attempt = 0; attempt <= MAX_RETRY; attempt++) {
    try {
      response = await fetch(url, {
        ...rest,
        credentials: 'include',
        headers: finalHeaders,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
      // Retry tylko 503 (transient infra) - inne errory zostawiamy
      if (response.status === 503 && attempt < MAX_RETRY) {
        const retryAfter = response.headers.get('Retry-After');
        const delayMs = retryAfter ? Number(retryAfter) * 1000 : 500 * (attempt + 1);
        await new Promise((r) => setTimeout(r, Math.min(delayMs, 2000)));
        continue;
      }
      break;
    } catch (err) {
      // Network failure (offline, DNS, etc.) - retry tylko dla GET
      lastNetworkError = err;
      if (attempt < MAX_RETRY) {
        await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
        continue;
      }
      throw err;
    }
  }
  if (!response) {
    throw lastNetworkError ?? new Error('Brak połączenia z serwerem');
  }

  // 401 — niech AuthGuard / wywolawca obsluzy (NIE robimy reload tutaj —
  // CF Access przechwytuje request po stronie edge, dla dev pokazujemy login UI)

  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // not JSON — zostaw text
      parsed = text;
    }
  }

  if (!response.ok) {
    const errorBody = parsed as ApiError | null;
    const code = errorBody?.error?.code ?? 'UNKNOWN';
    const message =
      errorBody?.error?.message ??
      describeValidationError(parsed) ??
      `Serwer zwrócił błąd (${response.status})`;
    throw new ApiClientError(response.status, code, message, errorBody?.error?.details);
  }

  // Successful — zakladamy ApiResponse<T> shape
  if (parsed && typeof parsed === 'object' && 'data' in parsed) {
    return (parsed as ApiResponse<T>).data as T;
  }

  return parsed as T;
}

/**
 * Wariant dla list — zwraca Paginated<T> z meta (total, page, pageSize).
 */
export async function apiFetchPaginated<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<Paginated<T>> {
  const { body, query, headers, ...rest } = options;

  let url = path;
  if (query) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    }
    const qs = params.toString();
    if (qs) url += (url.includes('?') ? '&' : '?') + qs;
  }

  const finalHeaders = new Headers(headers);
  if (body !== undefined && !finalHeaders.has('Content-Type')) {
    finalHeaders.set('Content-Type', 'application/json');
  }
  finalHeaders.set('Accept', 'application/json');

  const response = await fetch(url, {
    ...rest,
    credentials: 'include',
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const json = (await response.json()) as ApiResponse<T[]> | ApiError;
  if (!response.ok) {
    const err = json as ApiError;
    throw new ApiClientError(
      response.status,
      err.error?.code ?? 'UNKNOWN',
      err.error?.message ??
        describeValidationError(json) ??
        `Serwer zwrócił błąd (${response.status})`,
      err.error?.details,
    );
  }

  const success = json as ApiResponse<T[]>;
  return {
    data: success.data,
    meta: {
      total: success.meta?.total ?? success.data.length,
      page: success.meta?.page ?? 1,
      pageSize: success.meta?.pageSize ?? success.data.length,
    },
  };
}
